import { defineStore, StateTree } from 'pinia';
import dayjs, { Dayjs } from 'dayjs';
import { CampayaCoreApiClient } from "@/services/campayacore-api.service"
import { getText } from '@/services/translation.service';
import { destr } from "destr";
import { formatDate, formatDateRange } from '@/services/formatting.service';
const apiClient = new CampayaCoreApiClient();

export const useSearchStore = defineStore('search', {
  state: () => ({
    filters: {
      areaId: 3584,
      areaName: undefined as string | undefined,
      searchQuery: null as string | null,
      fromDate: null as Dayjs | null,
      toDate: null as Dayjs | null,
      flexPeriod: 0 as number,
      countryId: null,
      persons: {
        adults: 2,
        children: 0,
        infants: 0,
        pets: 0,
        confirmed: false,
      },
      previousPersons: {
        adults: 2,
        children: 0,
        infants: 0,
        pets: 0,
      },
    },
    modals: {
      datePickerModal: false,
      destinationModal: false,
      personModal: false,
      autoCompleteModal: false,
    },
    destinations: {
      countries: [],
      areas: {},
      breadcrumbs: [],
      shownAreas: [],
      countriesLoading: false,
      areasLoading: false,
      selectedCountry: 3584, // Denmark
      autoCompleteList: {
        loading: false,
        items: [],
      },
      countrySelectedOnMobile: false,
    },
    autocompleteSuggestions: {
      loading: false,
      error: false,
      suggestions: [],
    },
    newSearchRequired: false,
  }),
  actions: {
    toggleModal(modal: string, state: boolean) {
      this.modals[modal] = state;
    },
    updateFilter(key: string, value: any, updateURL: boolean = true) {
      this.filters[key] = value;
      if (updateURL) this.updateURLSearchParam(key, value);
    },
    setNewSearchRequired(newSearchRequired: boolean) {
      this.newSearchRequired = newSearchRequired;
    },
    setFromDate(date: string) {
      this.filters.fromDate = dayjs(date);
      this.updateURLSearchParam('fromDate', this.filters.fromDate.format('YYYY-MM-DD'));
    },
    setToDate(date: string) {
      this.filters.toDate = dayjs(date);
      this.updateURLSearchParam('toDate', this.filters.toDate.format('YYYY-MM-DD'));
    },
    clearDates() {
      this.filters.fromDate = null;
      this.filters.toDate = null;
      this.updateURLSearchParam('fromDate', null);
      this.updateURLSearchParam('toDate', null);
    },
    setSelectedCountry(areaId: number, updateFilter: boolean = false) {
      this.destinations.selectedCountry = areaId;
      this.destinations.countrySelectedOnMobile = true;
      if (updateFilter) this.updateURLSearchParam('countryId', areaId);
    },
    setAreaId(areaId: number) {
      this.filters.areaId = areaId;
      this.updateURLSearchParam('areaId', areaId);
    },
    setPersonsConfirmed(confirmed: boolean) {
      this.filters.persons.confirmed = confirmed;
      this.updateURLSearchParam('personsConfirmed', confirmed.toString());

      this.updateURLSearchParam('adults', this.filters.persons.adults.toString());
      this.updateURLSearchParam('children', this.filters.persons.children.toString());
      this.updateURLSearchParam('infants', this.filters.persons.infants.toString());
      this.updateURLSearchParam('pets', this.filters.persons.pets.toString());
    },
    updatePersonFilters(key: string, value: number) {
      this.filters.persons[key] = value;
      this.updateURLSearchParam(key, value.toString());
    },
    newDatesSameAsOld(newFromDate, newToDate) {
      return this.filters.fromDate?.isSame(newFromDate, 'day') && this.filters.toDate?.isSame(newToDate, 'day');
    },
    setupFiltersInState(params: URLSearchParams) {
      const fromDate = params.get('fromDate') || params.get('from');
      const toDate = params.get('toDate') || params.get('to');
      const areaId = params.get('areaId');

      let defaultArea = 3584;
      const themeId: any = document.getElementById('themeId');
      if (themeId && themeId.textContent.length >= 1) {
        if (themeId.textContent == '82') {
          defaultArea = 25;
          this.destinations.selectedCountry = null;
        }
      }

      this.filters.areaId = areaId ? +areaId : defaultArea;
      this.filters.areaName = params.get('areaName') ?? undefined;
      this.filters.fromDate = fromDate ? dayjs(fromDate) : this.filters.fromDate;
      this.filters.toDate = toDate ? dayjs(toDate) : this.filters.toDate;

      const persons = {
        adults: Number(params.get('adults')) || this.filters.persons.adults,
        children: Number(params.get('children')) || this.filters.persons.children,
        infants: Number(params.get('infants')) || this.filters.persons.infants,
        pets: Number(params.get('pets')) || this.filters.persons.pets,
        confirmed: params.get('personsConfirmed') === 'true' || this.filters.persons.confirmed,
        changed: false,
      }

      this.filters.persons = { ...persons };
      this.filters.previousPersons = { ...persons };

      // Destinations
      if (params.get('countryId')) {
        this.destinations.selectedCountry = Number(params.get('countryId'));
      }

      if (!areaId && params.get('countryId')) {
        this.updateFilter('areaId', +params.get('countryId'));
      }
      this.destinations.countrySelectedOnMobile = !!params.get('countryId');
    },
    setupUrlFromFilters() {
      if (this.destinations.selectedCountry) {
        this.updateURLSearchParam("countryId", this.destinations.selectedCountry.toString());
      }
      if (this.filters.persons.adults !== undefined && this.filters.persons.adults != 2) {
        this.updateURLSearchParam("adults", this.filters.persons.adults.toString());
      }
      if (this.filters.persons.children !== undefined && this.filters.persons.children != 0) {
        this.updateURLSearchParam("children", this.filters.persons.children.toString());
      }
      if (this.filters.persons.infants !== undefined && this.filters.persons.infants != 0) {
        this.updateURLSearchParam("infants", this.filters.persons.infants.toString());
      }
      if (this.filters.persons.pets !== undefined && this.filters.persons.pets != 0) {
        this.updateURLSearchParam("pets", this.filters.persons.pets.toString());
        this.updateURLSearchParam('petsAllowed', true);
      }
      if (this.filters.persons.confirmed) {
        this.updateURLSearchParam('personsConfirmed', true);
      } 
      if (this.filters.fromDate) {
        this.updateURLSearchParam("fromDate", this.filters.fromDate.format('YYYY-MM-DD'));
      }
      if (this.filters.toDate) {
        this.updateURLSearchParam("toDate", this.filters.toDate.format('YYYY-MM-DD'));
      }
    },
    updateURLSearchParam(param: string, newValue: any, deleteParam: boolean = true) {
      const urlParams = new URLSearchParams(window.location.search);

      if (newValue && (newValue > 0 || newValue.length > 0 || newValue === '0')) {
        urlParams.set(param, newValue.toString())
      } else if (deleteParam) {
        urlParams.delete(param)
      }

      if (newValue === null) urlParams.delete(param);

      window.history.replaceState({}, '', `${location.pathname}?${urlParams}`)
    },
    updateSearhDatesIfBeforeToday() {
      if (this.filters.fromDate && this.filters.fromDate.isBefore(dayjs(), 'day')) {
        this.filters.fromDate = dayjs();
      }

      if (this.filters.toDate && this.filters.toDate.isBefore(dayjs(), 'day')) {
        this.filters.toDate = dayjs().add(7, 'day');
      }
    },
    updatePreviousPersons() {
      this.filters.previousPersons = { ...this.filters.persons };
    },
    updateFlexPeriod(flexPeriod: number) {
      this.filters.flexPeriod = flexPeriod;
      this.updateURLSearchParam('flexPeriod', flexPeriod.toString());
    },

    // Destinations
    async getCountries(continentId = 25) {
      this.destinations.countriesLoading = true;
      // 25 is id for Europe
      const res = await apiClient.relatedDestinations(continentId);
      this.destinations.countries = res.destinations;
      this.destinations.countriesLoading = false;
    },
    async apiGetRelatedDestinations(areaId: number, relatedThemeId?: number) {
      return await apiClient.relatedDestinations(areaId, relatedThemeId)
    },
    async getRelatedDestinations(
      relatedAreaId: number,
      triggerModals: boolean = false,
      allowFullSearch: boolean = true,
      setCountryId: boolean = false,
      relatedThemeId: number | undefined = undefined
    ) {
      if (!relatedAreaId) return false;

      this.destinations.areasLoading = true;
      if (this.destinations.areas[relatedAreaId]) {
        if (this.destinations.areas[relatedAreaId].destinations.length) {
          this.destinations.shownAreas = this.destinations.areas[relatedAreaId].destinations;
          return true;
        }

        return false;
      };

      let hasDestinations = true;
      const fetchDestinations = async (areaId: number, depth: number = 0): Promise<any> => {
        let res = await apiClient.relatedDestinations(areaId, relatedThemeId);
        this.destinations.areas[areaId] = res;

        if (!allowFullSearch && res.breadcrumbs.length === 3) {
          return res;
        }

        if (!res.destinations.length && depth < res.breadcrumbs.length - 1) {
          hasDestinations = false;
          const parentAreaId = res.breadcrumbs[res.breadcrumbs.length - 2 - depth].areaId;
          return fetchDestinations(parentAreaId, depth + 1);
        }

        return res;
      };

      let res = await fetchDestinations(relatedAreaId);

      if (!res.destinations.length && triggerModals) {
        this.modals.destinationModal = false;
        this.modals.datePickerModal = true;
      }

      if (res.breadcrumbs.length && setCountryId) {
        this.updateFilter('countryId', res.breadcrumbs[0].areaId);
        this.destinations.selectedCountry = res.breadcrumbs[0].areaId;
      }
      this.destinations.shownAreas = res.destinations;
      return hasDestinations;
    },
    async getRelatedPropertyDestinations(propertyId: string) {
      this.destinations.areasLoading = true;
      const res = await apiClient.propertyRelatedDestinations(propertyId);
      this.destinations.shownAreas = res.destinations;
      this.destinations.areasLoading = false;
    },
    clearDestinationInput() {
      this.updateFilter('searchQuery', null);
      this.updateFilter('areaId', null);
      this.updateFilter('areaName', null);
      this.destinations.countrySelectedOnMobile = null;

      // reset the shown areas to denmarks results
      if (this.destinations.areas[3584]) {
        this.destinations.shownAreas = this.destinations.areas[3584].destinations;

        this.destinations.selectedCountry = 3584;
      } else {
        this.getRelatedDestinations(3584);
      }
    },
    seeEntireCountry(countryAreaId: number) {
      this.updateFilter('areaId', countryAreaId);
      this.updateFilter('areaName', this.destinations.countries.find((country) => country.areaId === countryAreaId)?.title);

      this.getRelatedDestinations(countryAreaId);
      this.toggleModal('destinationModal', false);

      if (!this.filters.fromDate) {
        this.modals.datePickerModal = true;
      }
    },

    async fetchSuggestions(searchQuery: string, isMobile = false) {
      this.destinations.autoCompleteList.loading = true;
      if (!isMobile) {
        this.toggleModal('destinationModal', false);
        this.toggleModal('autoCompleteModal', true);
      }

      const res = await apiClient.autocomplete(searchQuery).finally(() => {
        this.destinations.autoCompleteList.loading = false;
      });

      this.destinations.autoCompleteList.items = res || [];
    }
  },
  getters: {
    isPersonsConfirmed(state) {
      return !!(state.filters.persons.confirmed && (state.filters.fromDate && state.filters.toDate));
    },
    formattedPersons(state) {
      let formattedGuests = "";

      const { adults, children, infants, pets, confirmed } = state.filters.persons;

      const personsSingularPluralTable = {
        adults: [ getText(13504), getText(1445) ],
        children: [ getText(8060), getText(8059) ],
        pets: [ getText(13875), getText(13875) ],
      }

      if (!confirmed) {
        return getText(13883)
      }

      if (adults > 0) {
        formattedGuests += `${adults} ${personsSingularPluralTable.adults[adults === 1 ? 0 : 1]}`;
      }

      if (children + infants > 0) {
        if (formattedGuests.length > 0) {
          formattedGuests += `, `;
        }

        formattedGuests += `${children + infants} ${personsSingularPluralTable.children[children + infants === 1 ? 0 : 1]}`;
      }

      if (pets > 0) {
        if (formattedGuests.length > 0) {
          formattedGuests += `, `;
        }

        formattedGuests += `${pets} ${personsSingularPluralTable.pets[pets === 1 ? 0 : 1]}`;
      }

      if (formattedGuests === "") {
        formattedGuests = getText(13883);
      }

      return formattedGuests;
    },
    formattedDate(state) {
      if (!state.filters.fromDate || !state.filters.toDate) {
        // Fallback for frontpage as translation store is slow to load
        return getText(13876);
      }
      
      return formatDateRange(state.filters.fromDate.toDate(), state.filters.toDate.toDate());

      // if (state.filters.fromDate.isSame(state.filters.toDate, 'month')) {
      //   return `${state.filters.fromDate.format('D')} - ${state.filters.toDate.format('D. MMM')}`
      // }

      // if (state.filters.fromDate.isSame(state.filters.toDate, 'year')) {
      //   return `${state.filters.fromDate.format('D. MMM')} - ${state.filters.toDate.format('D. MMM')}`
      // }

      // return `${state.filters.fromDate.format('D/M-YY')} - ${state.filters.toDate.format('D/M-YY')}`
    },
    formattedCheckInDate(state) {
      if (!state.filters.fromDate) {
        return getText(13876);
      }
      return formatDate(state.filters.fromDate.toDate());
    },
    formattedNights(state) {
      if (!state.filters.fromDate || !state.filters.toDate) {
        return '';
      }
      const nights = state.filters.toDate.diff(state.filters.fromDate, 'day');
      if (nights === 1) {
        return getText(14903);
      }
      return getText(14902)?.replace("[Nights]", nights.toString());
    },
    isPersonsUpdated(state) {
      if (state.filters.persons.adults !== state.filters.previousPersons.adults) {
        return true;
      }

      if (state.filters.persons.children !== state.filters.previousPersons.children) {
        return true;
      }

      if (state.filters.persons.infants !== state.filters.previousPersons.infants) {
        return true;
      }

      if (state.filters.persons.pets !== state.filters.previousPersons.pets) {
        return true;
      }

      return false;
    },

    // Destinations
    getSelectedAreas(state) {
      if (!state.destinations.shownAreas) return [];
      return state.destinations.shownAreas ?? [];
    },
    getBreadcrumbs(state) {
      let areaId = state.filters.areaId ?? 3584;
      if (!state.destinations.areas[areaId]) return [];

      return state.destinations.areas[areaId].breadcrumbs.map(bc => {
        return {
          title: bc.areaName,
          areaId: bc.areaId,
          hasDestinations: bc.areaId != areaId || state.destinations.areas[areaId].destinations.length > 0
        }
      }) ?? [];
    },
    getBreadcrumbsWithoutRoot(state) {
      let areaId = state.filters.areaId ?? 3584;
      if (!state.destinations.areas[areaId]) return [];

      return state.destinations.areas[areaId].breadcrumbs.filter((x: any) => x.areaId != this.destinations.selectedCountry) ?? [];
    },
    getSelectedCountry(state) {
      return state.destinations.countries.find((country) => country.areaId === state.destinations.selectedCountry);
    }
  },
  persist: {
    storage: sessionStorage,
    paths: ['filters.fromDate', 'filters.toDate', 'filters.persons', 'filters.previousPersons', 'filters.flexPeriod'],
    serializer: {
      serialize: (value) => {
        return JSON.stringify(value)
      },
      deserialize: (value) => {
        let deserialized = destr<StateTree>(value);

        const fromDate = dayjs(deserialized.filters.fromDate);
        const toDate = dayjs(deserialized.filters.toDate)
        if (fromDate.isValid()) {
          deserialized.filters.fromDate = fromDate;
        }
        if (toDate.isValid()) {
          deserialized.filters.toDate = toDate;
        }

        return deserialized;
      }
    }
  }
})
